import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { APP_ROUTES } from '@/common/constants'
import { AccountType, Account } from '@/common/types'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import AdviserClientsAddAccountClickedAnalytics from '@/common/analytics/events/AdviserClientsAddAccountClickedAnalytics'
import AdviserClientsResumeKycClickedAnalytics from '@/common/analytics/events/AdviserClientsResumeKycClickedAnalytics'
import { ClientTab } from './types'
import { isActiveAccount } from './utils/isActiveAccount'

export type AccountInfo = {
    accountId: string
    accountName: string
    accountType: AccountType
}

type Result = {
    // Account data
    account: Account | null
    accounts: AccountInfo[]
    activeAccountId: string | null
    primaryAccount: Account | null
    // Tab state
    activeTab: ClientTab
    setActiveTab: (tab: ClientTab) => void
    // Data
    clientAccounts: AccountInfo[]
    inProgressClients: Account[]
    // Computed values
    hasClientAccounts: boolean
    isPrimaryAdviserContext: boolean
    isAdviserWorkspaceAccount: boolean
    // Handlers
    handleAddClient: () => void
    handleSelectAccount: (accountId: string) => Promise<void>
    handleResumeClient: (accountId: string) => Promise<void>
}

export function useClientsLogic(): Result {
    const navigate = useNavigate()
    const switchAccount = useAccountSwitcher()
    const account = useCurrentAccount()
    const accounts = useAccountStore((s) => s.getAccounts())
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const [activeTab, setActiveTab] = useState<ClientTab>(ClientTab.ACTIVE)

    const isPrimaryAdviserContext =
        primaryAccount?.accountType === AccountType.ADVISER &&
        (!activeAccountId || activeAccountId === primaryAccount.accountId)
    const isAdviserWorkspaceAccount = account?.accountType === AccountType.ADVISER || isPrimaryAdviserContext

    const clientAccounts = useMemo(
        () => accounts.filter((account) => account.accountType !== AccountType.ADVISER && isActiveAccount(account)),
        [accounts],
    )

    const inProgressClients = useMemo<Account[]>(
        () => accounts.filter((account) => account.accountType !== AccountType.ADVISER && !isActiveAccount(account)),
        [accounts],
    )

    const hasClientAccounts = clientAccounts.length > 0

    const handleAddClient = useCallback(() => {
        AdviserClientsAddAccountClickedAnalytics.track()
        navigate(APP_ROUTES.APPLICATION_CREATE)
    }, [navigate])

    const handleSelectAccount = useCallback(
        async (accountId: string) => {
            if (useAccountStore.getState().isAccountSwitching) return

            if (accountId === activeAccountId) {
                navigate(APP_ROUTES.DASHBOARD)
                return
            }

            await switchAccount(accountId)
            navigate(APP_ROUTES.DASHBOARD)
        },
        [activeAccountId, navigate, switchAccount],
    )

    const handleResumeClient = useCallback(
        async (accountId: string) => {
            AdviserClientsResumeKycClickedAnalytics.track()

            if (accountId === activeAccountId) {
                navigate(APP_ROUTES.APPLICATION_KYC)
                return
            }

            await switchAccount(accountId)
            navigate(APP_ROUTES.APPLICATION_KYC)
        },
        [activeAccountId, navigate, switchAccount],
    )

    return {
        account,
        accounts,
        activeAccountId,
        primaryAccount,
        activeTab,
        setActiveTab,
        clientAccounts,
        inProgressClients,
        hasClientAccounts,
        isPrimaryAdviserContext,
        isAdviserWorkspaceAccount,
        handleAddClient,
        handleSelectAccount,
        handleResumeClient,
    }
}
